/**
 * Os atributos de uma categoria de marketplace.
 *
 * **Um componente, quatro marketplaces** — e o quinto entra por configuração,
 * não por código novo. Hoje são cinco componentes de ~1.570 linhas somadas
 * (Shopee, B2W, TikTok, ViaVarejo, Meli), cada um resolvendo o mesmo problema
 * porque cada marketplace dá nome diferente aos mesmos conceitos.
 *
 * A leitura do JSON é pura e mora em `attribute-set.ts`, com teste sobre os
 * payloads reais dos quatro. Aqui é só o desenho.
 *
 * **A substituição é firme quanto ao ritmo:** um marketplace por vez, comparado
 * lado a lado com o componente existente, e nenhum apagado antes de o novo estar
 * aprovado naquele marketplace. Trocar cinco integrações num commit é o tipo de
 * risco que não se paga.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTagModule } from 'ng-zorro-antd/tag';

import {
  BUILTIN_ATTRIBUTE_PROFILES
} from '../../model/attribute-schema';
import { ScreenNode } from '../../model/node-props';
import { NodeRenderer, RenderMode } from '../node-renderer';
import { ScreenContext } from '../screen-context';
import { TranslateTAPipe } from '../translate-ta.pipe';
import {
  ResolvedAttribute,
  groupAttributes,
  resolveAttributes
} from './attribute-set';

@Component({
  selector: 'tem-attribute-set',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzCheckboxModule,
    NzEmptyModule,
    NzInputModule,
    NzSelectModule,
    NzTagModule,
    TranslateTAPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="tem-attrs">

      @if (isSearchable() || canFilterRequired()) {
        <div class="tem-attrs__bar">
          @if (isSearchable()) {
            <input
              nz-input
              nzSize="small"
              type="search"
              [ngModel]="search()"
              (ngModelChange)="search.set($event)"
              [placeholder]="'builderScreen.attributes.search' | translateTA" />
          }

          @if (canFilterRequired()) {
            <label nz-checkbox
              [ngModel]="onlyRequired()"
              (ngModelChange)="onlyRequired.set($event)">
              {{ 'builderScreen.attributes.onlyRequired' | translateTA }}
            </label>
          }
        </div>
      }

      @if (groups().length === 0) {
        <nz-empty
          [nzNotFoundContent]="'builderScreen.attributes.empty' | translateTA" />
      }

      @for (group of groups(); track group.name) {
        <section class="tem-attrs__group">
          @if (group.name) {
            <h4>{{ group.name }}</h4>
          }

          @for (attribute of group.attributes; track attribute.id) {
            <div class="tem-attrs__row">
              <label class="tem-attrs__label">
                {{ attribute.label }}
                @if (attribute.required) {
                  <span class="tem-attrs__required">*</span>
                }
              </label>

              @if (attribute.options.length > 0) {
                <nz-select
                  nzSize="small"
                  [nzMode]="attribute.multiple ? 'multiple' : 'default'"
                  [nzShowSearch]="true"
                  [nzAllowClear]="!attribute.required"
                  [ngModel]="valueOf(attribute.id)"
                  (ngModelChange)="setValue(attribute.id, $event)">
                  @for (option of attribute.options; track option.value) {
                    <nz-option [nzValue]="option.value" [nzLabel]="option.label"></nz-option>
                  }
                </nz-select>
              } @else {
                <!-- Sem lista de opções, o atributo é texto livre. Acontece com
                     medida, código próprio e observação — e é o padrão do
                     marketplace, não uma falha de configuração. -->
                <input
                  nz-input
                  nzSize="small"
                  [ngModel]="valueOf(attribute.id)"
                  (ngModelChange)="setValue(attribute.id, $event)" />
              }

              @if (attribute.units.length > 0) {
                <nz-select
                  nzSize="small"
                  class="tem-attrs__unit"
                  [ngModel]="unitOf(attribute.id)"
                  (ngModelChange)="setUnit(attribute.id, $event)">
                  @for (unit of attribute.units; track unit.value) {
                    <nz-option [nzValue]="unit.value" [nzLabel]="unit.label"></nz-option>
                  }
                </nz-select>
              }
            </div>

            <!-- Os filhos só aparecem quando um valor do pai foi escolhido: é o
                 aninhamento da Shopee, em que a escolha do pai muda quais
                 atributos existem. -->
            @if (attribute.children.length > 0 && valueOf(attribute.id)) {
              <div class="tem-attrs__children">
                @for (child of attribute.children; track child.id) {
                  <div class="tem-attrs__row">
                    <label class="tem-attrs__label">{{ child.label }}</label>
                    <input
                      nz-input
                      nzSize="small"
                      [ngModel]="valueOf(child.id)"
                      (ngModelChange)="setValue(child.id, $event)" />
                  </div>
                }
              </div>
            }
          }
        </section>
      }
    </div>
  `,
  styleUrl: './attribute-set.renderer.scss'
})
export class AttributeSetRenderer implements NodeRenderer<'attributeSet'> {

  readonly node = input.required<ScreenNode<'attributeSet'>>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  private readonly context = inject(ScreenContext, { optional: true });

  readonly search = signal('');
  readonly onlyRequired = signal(false);

  /** O que o usuário escolheu, por id de atributo. */
  private readonly chosen = signal<Readonly<Record<string, unknown>>>({});
  private readonly units = signal<Readonly<Record<string, string>>>({});

  readonly isSearchable = computed<boolean>(() => this.node().props.searchable === true);

  readonly canFilterRequired = computed<boolean>(
    () => this.node().props.onlyRequiredByDefault !== undefined
  );

  /**
   * A leitura efetiva: o perfil nativo, com o que o nó sobrescreve.
   *
   * O perfil resolve o caso comum — escolher "Shopee" e pronto — e a
   * sobrescrita ponto a ponto resolve o marketplace novo, sem componente novo.
   */
  private readonly reading = computed(() => {
    const props = this.node().props;
    const perfil = BUILTIN_ATTRIBUTE_PROFILES.find(
      candidato => candidato.id === props.profileId
    );

    return {
      sourcePath: props.sourcePath ?? perfil?.sourcePath,
      nesting: props.nesting ?? perfil?.nesting,
      fields: { ...(perfil?.fields ?? {}), ...(props.fields ?? {}) } as never
    };
  });

  private readonly attributes = computed<readonly ResolvedAttribute[]>(() => {
    const caminho = this.node().binding?.dataPath;

    if (this.context === null) {
      return [];
    }

    const dados = caminho === undefined
      ? this.context.data()
      : readFrom(this.context.data(), caminho);

    return resolveAttributes(dados, this.reading());
  });

  readonly groups = computed(() => {
    const alvo = this.search().trim().toLowerCase();
    const soObrigatorios = this.onlyRequired();

    const filtrados = this.attributes().filter(atributo => {
      if (soObrigatorios && !atributo.required) {
        return false;
      }

      if (alvo.length === 0) {
        return true;
      }

      return atributo.label.toLowerCase().includes(alvo);
    });

    return groupAttributes(filtrados);
  });

  valueOf(id: string): unknown {
    return this.chosen()[id] ?? null;
  }

  unitOf(id: string): string {
    return this.units()[id] ?? '';
  }

  setValue(id: string, value: unknown): void {
    this.chosen.set({ ...this.chosen(), [id]: value });
  }

  setUnit(id: string, unit: string): void {
    this.units.set({ ...this.units(), [id]: unit });
  }
}

function readFrom(source: unknown, path: string): unknown {
  let current = source;

  for (const segment of path.split('.')) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return null;
    }
    current = (current as Record<string, unknown>)[segment];
  }

  return current;
}
